import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { STATE_COORDS } from "../../lib/network";

// Fixed SVG canvas so nodes sit on the same India outline used elsewhere.
const W = 260, H = 300;
const LNG0 = 68, LNG1 = 98, LAT0 = 6, LAT1 = 38;
const project = ([lng, lat]) => [
  ((lng - LNG0) / (LNG1 - LNG0)) * W,
  ((LAT1 - lat) / (LAT1 - LAT0)) * H,
];

/**
 * WalletNetworkPulse
 * ──────────────────
 * A compact 260×300 map of India that pulses a coloured node for every
 * recent debit — visually connecting the wallet spend to logistics
 * links across Vinci's 20-state supplier network.
 *
 * Debits from the statement supply the pulse cadence. If nothing is
 * available we cycle a demo route so the widget never feels dead.
 */
const DEMO_ROUTE = ["Maharashtra", "Karnataka", "Gujarat", "Tamil Nadu",
                     "Uttar Pradesh", "West Bengal", "Rajasthan", "Delhi"];

export function WalletNetworkPulse({ statement = [], tier }) {
  const debitStates = useMemo(() => {
    const s = statement
      .filter((r) => r.type === "debit" && r.state)
      .map((r) => r.state);
    return s.length > 0 ? s.slice(0, 24) : DEMO_ROUTE;
  }, [statement]);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const mq = typeof window !== "undefined"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (mq) return;
    const t = setInterval(() => setIdx((n) => n + 1), 1600);
    return () => clearInterval(t);
  }, []);

  const activeState = debitStates[idx % debitStates.length];
  const active = STATE_COORDS[activeState];
  const activeXY = active ? project(active) : null;

  return (
    <div
      data-testid="wallet-network-pulse"
      className="relative border rounded-sm p-5 overflow-hidden"
      style={{
        background: `linear-gradient(135deg,rgba(255,255,255,0.03),rgba(0,0,0,0.4))`,
        borderColor: `${tier.primary}33`,
        boxShadow: `inset 0 0 60px ${tier.glow}22`,
      }}
    >
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div
            className="font-grotesk text-[9px] uppercase tracking-[0.32em]"
            style={{ color: tier.accentSoft }}
          >
            Network Power
          </div>
          <div className="font-serif text-lg text-[#F5F5F0] mt-0.5">
            Logistics Grid
          </div>
        </div>
        <div className="text-right">
          <div
            className="font-grotesk text-[9px] uppercase tracking-[0.22em]"
            style={{ color: tier.accent }}
          >
            {activeState || "—"}
          </div>
          <div className="text-[10px] font-mono text-[#6f6f68]">
            Live spend link
          </div>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <defs>
          <radialGradient id="wnp-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={tier.primary} stopOpacity="0.25" />
            <stop offset="100%" stopColor={tier.primary} stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* soft aura */}
        <rect width={W} height={H} fill="url(#wnp-glow)" />

        {/* every state as a faint dot */}
        {Object.entries(STATE_COORDS).map(([name, coords]) => {
          const [x, y] = project(coords);
          const isActive = name === activeState;
          return (
            <g key={name}>
              <circle
                cx={x} cy={y} r={isActive ? 3.6 : 2}
                fill={isActive ? tier.primary : "#8A8A82"}
                fillOpacity={isActive ? 1 : 0.35}
                style={{
                  filter: isActive
                    ? `drop-shadow(0 0 8px ${tier.glow})`
                    : "none",
                }}
              />
            </g>
          );
        })}

        {/* pulsing ripple on the active node */}
        {activeXY && (
          <motion.circle
            key={`${activeState}-${idx}`}
            cx={activeXY[0]} cy={activeXY[1]}
            r={4}
            fill="none"
            stroke={tier.accent}
            strokeWidth="1.2"
            initial={{ scale: 0.6, opacity: 0.9 }}
            animate={{ scale: 6, opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            style={{ transformOrigin: `${activeXY[0]}px ${activeXY[1]}px` }}
          />
        )}
      </svg>

      <div className="mt-2 text-[10px] font-body text-[#8A8A82] italic">
        Every debit ripples across Vinci's PAN-India supplier grid.
      </div>
    </div>
  );
}
